import { useTranslation } from 'react-i18next';
import { Box, Group, Stack, Text } from '@mantine/core';
import { formatMoney } from '@/lib/format';
import type { BestPriceTone } from '@/types/bestPrice';
import classes from './BestPriceLabel.module.css';

/** Сторона действия лучшей цены: продавец получает FCA, покупатель платит CPT. */
export type BestPriceAction = 'buy' | 'sell';

export type { BestPriceTone };

interface BestPriceLabelProps {
  /** Лучшая встречная цена, ₽/т (для sell — FCA у ворот, для buy — CPT с доставкой). */
  readonly price: number;
  /** Что делает пользователь: 'sell' → «Продать по X», 'buy' → «Купить по X». */
  readonly action: BestPriceAction;
  /** Визуальный тир (по умолчанию `plain` — без акцента). */
  readonly tone?: BestPriceTone;
  /**
   * Выгода относительно МОЕЙ цены, ₽/т (со знаком; >0 — выигрыш). Передаётся только там, где лучшая
   * цена привязана к моему офферу/прайсу; в поиске (привязки нет) — не передаётся, дельту не рисуем.
   */
  readonly advantage?: number;
  /**
   * Вид элемента: `cta` — «Продать/Купить по X» как кнопка (списки/дашборд/грид/поиск, ховер на кнопке);
   * `static` — крупная оранжевая цена без обводки (превью встречного предложения, не кликабельна).
   */
  readonly variant?: 'cta' | 'static';
  /**
   * Единая ширина кнопок в колонке (min-width под самую длинную), контент по правому краю. Для дашборда:
   * «Купить по 4 567» и «Продать по 16 533» одинаковой ширины, без лишней пустоты. Колонка — `align="flex-end"`.
   */
  readonly block?: boolean;
}

// Единый элемент «лучшей цены» (см. design-guidelines §5): «Продать по X» / «Купить по X» оформлено как
// кнопка. ЦВЕТ — единый ОРАНЖЕВЫЙ (ask) для обеих сторон. ВЕС заливки — акцент: выгодная/лучшая (tone
// gain) — залитая, рынок/поиск — мягкая. Число-цена — герой кнопки (крупнее verb). Под кнопкой — дельта
// «к вашей цене» (зелёный выигрыш ↗ / красный потеря ↘). На превью та же цена — крупным оранжевым числом
// (`variant="static"`). Презентационный: оборачивается в BestPriceLink.
export default function BestPriceLabel({ price, action, tone = 'plain', advantage, variant = 'cta', block = false }: BestPriceLabelProps) {
  const { t } = useTranslation();
  const verb = t(action === 'sell' ? 'app.bestPrice.sellFor' : 'app.bestPrice.buyFor');
  const emphasized = tone === 'gain';
  const isStatic = variant === 'static';

  return (
    <Stack gap={4} align={isStatic ? 'flex-start' : 'flex-end'} style={{ minWidth: 0 }}>
      {isStatic ? (
        // Превью: крупная оранжевая цена (без обводки) — главный hero-номер страницы.
        <Group gap={6} align="baseline" wrap="nowrap">
          <Text fz={28} fw={700} c="ask.7" lh={1.1}>
            {formatMoney(price)}
          </Text>
          <Text size="sm" c="gray.8">
            ₽/т
          </Text>
        </Group>
      ) : (
        <Box className={`${classes.cta} ${emphasized ? classes.filled : classes.soft} ${block ? classes.block : ''}`}>
          {/* Цвет — из класса кнопки (не задаём `c` на span).
              Число — герой: крупнее (≈18) и жирнее глагола; глагол — тихая подпись. */}
          <Text span lh={1.15}>
            <Text span fz={13} fw={600}>
              {verb}{' '}
            </Text>
            <Text span fz={18} fw={700}>
              {formatMoney(price)}
            </Text>
          </Text>
        </Box>
      )}
      {advantage != null ? <AdvantageHint advantage={advantage} action={action} /> : null}
    </Stack>
  );
}

// Дельта под кнопкой «лучшей цены»: насколько рыночная цена ВЫШЕ/НИЖЕ моей. СЛОВО несёт направление
// («Дороже»/«Дешевле» на N), ЦВЕТ — выгоду: зелёный — выгодно, красный — невыгодно, серый — ровно моя
// цена. `advantage` нормализован так, что >0 = ВЫИГРЫШ для обеих сторон; но «дороже/дешевле» зависит и
// от стороны: продавцу выигрыш = рынок дороже, покупателю выигрыш = рынок дешевле. Без стрелок (слово
// уже несёт направление). Цвет — тон 7 (контраст AA на белом; тон 6 не проходит).
export function AdvantageHint({ advantage, action }: { readonly advantage: number; readonly action: BestPriceAction }) {
  const { t } = useTranslation();

  // Битое значение (NaN/Infinity из не распарсенной строки бэкенда) — лучше ничего.
  if (!Number.isFinite(advantage)) {
    return null;
  }

  // Разница в пределах рубля (округляется к 0) — «ровно моя цена», нейтрально.
  if (Math.round(advantage) === 0) {
    return (
      <Text size="sm" fw={600} c="gray.7">
        {t('app.bestPrice.exact')}
      </Text>
    );
  }

  const isGain = advantage > 0;
  // Рынок ДОРОЖЕ моей цены, когда: продажа+выигрыш (платят больше) ИЛИ покупка+проигрыш (просят больше).
  const marketHigher = (action === 'sell') === isGain;
  const color = isGain ? 'var(--mantine-color-gain-7)' : 'var(--mantine-color-loss-7)';
  const text = t(marketHigher ? 'app.bestPrice.higherBy' : 'app.bestPrice.lowerBy', {
    amount: formatMoney(Math.abs(advantage)),
  });

  return (
    <Text size="sm" fw={700} style={{ color }}>
      {text}
    </Text>
  );
}
